import {
  Autocomplete,
  Button,
  Grid2,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ReferenceRangeAutocomplete from '@components/Autocomplete/Type/ReferenceRangeAutocomplete/ReferenceRangeAutocomplete';
import UserAlertSettingAutocomplete from '@components/Autocomplete/Type/UserAlertSettingAutocomplete/UserAlertSettingAutocomplete';
import { FormikToggleField } from '@components/FormikFields';
import InputLabel from '@components/InputLabel/index';

import { getObservationConfig, VITAL_SIGN_TYPES } from '@constants/fhir';

import { useSession } from '@hooks/useSession';

import { useListReferenceRangeOptions } from 'generated/reference_range.generated';
import type { ReferenceRangeOptionModel } from 'generated/types';

import type { FormValues } from './FormDialog';

const messages = defineMessages({
  title: {
    id: 'alertSettingModalContent.title',
    defaultMessage: 'Create Alert(s)',
  },
  subtitle: {
    id: 'alertSettingModalContent.subtitle',
    defaultMessage:
      'Alerts will be sent via email/text to the provided recipients when the patient’s {metric} readings are within the selected reference ranges.',
  },
  modify: {
    id: 'alertSettingModalContent.modify',
    defaultMessage: 'Modify Parameters',
  },
  recipientsTitle: {
    id: 'alertSettingModalContent.recipients.title',
    defaultMessage: 'Recipients',
  },
  recipientsCaption: {
    id: 'alertSettingModalContent.recipients.caption',
    defaultMessage:
      'Email and/or text notifications about the alert will be sent to the following recipients:',
  },
  parametersTitle: {
    id: 'alertSettingModalContent.alertParameters.title',
    defaultMessage: 'Alert Parameters',
  },
  parametersCaption: {
    id: 'alertSettingModalContent.alertParameters.caption',
    defaultMessage:
      'An alert will be created when the patient’s readings are within any of the selected parameters.',
  },
  notifications: {
    id: 'alertSettingModalContent.notifications',
    defaultMessage: 'Notifications',
  },
  toggleTitle: {
    id: 'alertSettingModalContent.notifications.toggleHeader',
    defaultMessage:
      'Alert Notifications: {enabled, select, true{Enabled} other{Disabled}}',
  },
  toggleCaption: {
    id: 'alertSettingModalContent.notifications.toggleCaption',
    defaultMessage: 'Emails and texts will be sent to recipients',
  },
  orgRecipient: {
    id: 'alertSettingModalContent.notifications.orgRecipient',
    defaultMessage:
      'Email and/or text notifications have been configured for your organization. To include additional recipients, please add them below.',
  },
});

interface AlertSettingFormProps {
  isEditing: boolean;
}

export default function AlertSettingForm({ isEditing }: AlertSettingFormProps) {
  const { formatMessage } = useIntl();
  const { setFieldValue, values } = useFormikContext<FormValues>();
  const { metric } = values;
  const {
    state: { activeProfile },
  } = useSession();

  const { data } = useListReferenceRangeOptions();

  const metricOptionsMap = (data?.listReferenceRangeOptions ?? []).reduce<
    Map<
      string,
      ReferenceRangeOptionModel & {
        friendlyCode: string;
        friendlyCategory: string;
      }
    >
  >((acc, curr) => {
    const param = getObservationConfig(curr.code);
    acc.set(curr.reference_range_id, {
      ...curr,
      friendlyCode: formatMessage({ id: param?.messageId }),
      friendlyCategory: formatMessage({
        id: `alertSettingModalContent.category.${curr.category}`,
      }),
    });
    return acc;
  }, new Map());

  const metricAttributes =
    metric.code in VITAL_SIGN_TYPES ? VITAL_SIGN_TYPES[metric.code] : undefined;

  return (
    <Grid2 container spacing={2}>
      <Grid2
        size={{ xs: 12 }}
        sx={{
          textAlign: 'center',
        }}
      >
        <Typography color="primary.main" variant="h5">
          {formatMessage(messages.title)}
        </Typography>
        <Typography variant="caption">
          {formatMessage(messages.subtitle, {
            metric: metricAttributes
              ? formatMessage({
                  id: metricAttributes.messageId,
                }).toLowerCase()
              : '',
          })}
        </Typography>
      </Grid2>
      <Grid2
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Button
          disabled={!metric.code}
          onClick={() =>
            navigate(`/records/${metric.category}/${metric.code}/parameters`)
          }
          sx={{
            width: 'fit-content',
          }}
          variant="outlined"
        >
          {formatMessage(messages.modify)}
        </Button>
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        <InputLabel
          label={formatMessage(messages.parametersTitle)}
          required
          style={{ color: 'primary.main' }}
        />
        <Typography variant="caption">
          {formatMessage(messages.parametersCaption)}
        </Typography>
        <Grid2 container spacing={1}>
          <Grid2 size={{ xs: 12, sm: metric.code ? 4 : 12 }}>
            <Autocomplete
              disabled={isEditing}
              getOptionLabel={(option) =>
                metricOptionsMap.get(option)?.friendlyCode ?? ''
              }
              groupBy={(option) =>
                metricOptionsMap.get(option)?.friendlyCategory ?? ''
              }
              onChange={(_event, value) => {
                const refRange = metricOptionsMap.get(value ?? '');
                if (value && refRange) {
                  setFieldValue('metric', {
                    reference_range_id: value,
                    category: refRange.category,
                    code: refRange.code,
                  });
                } else {
                  setFieldValue('metric', {
                    reference_range_id: '',
                    category: '',
                    code: '',
                  });
                }
              }}
              options={Array.from(metricOptionsMap).map(
                ([reference_range_id]) => reference_range_id
              )}
              renderInput={(params) => <TextField {...params} />}
              value={metric.reference_range_id}
            />
          </Grid2>
          {metric.code && (
            <Grid2 size={{ xs: 12, sm: 8 }}>
              <ReferenceRangeAutocomplete
                category={metric.category}
                code={metric.code}
                limitChips={3}
                name="reference_ranges"
                required
              />
            </Grid2>
          )}
        </Grid2>

        {metric.code && (
          <Grid2 size={{ xs: 12 }}>
            <InputLabel
              label={formatMessage(messages.recipientsTitle)}
              required
              style={{ color: 'primary.main' }}
            />
            {values.has_org_recipient && (
              <Typography variant="body2">
                {formatMessage(messages.orgRecipient)}
              </Typography>
            )}
            <Typography variant="caption">
              {formatMessage(messages.recipientsCaption)}
            </Typography>
            <UserAlertSettingAutocomplete
              account_id={activeProfile?.account_id ?? ''}
              name="recipients"
              required={!values.has_org_recipient}
            />
          </Grid2>
        )}

        <Grid2 size={{ xs: 12 }}>
          <InputLabel
            label={formatMessage(messages.notifications)}
            required
            style={{ color: 'primary.main' }}
          />
          <FormikToggleField
            formProps={{
              labelPlacement: 'start',
            }}
            label={
              <Stack spacing={0}>
                <Typography variant="body1">
                  {formatMessage(messages.toggleTitle, {
                    enabled: values.notification_enabled,
                  })}
                </Typography>
                <Typography variant="caption">
                  {formatMessage(messages.toggleCaption)}
                </Typography>
              </Stack>
            }
            name="notification_enabled"
            onChange={(value) => setFieldValue('notification_enabled', value)}
          />
        </Grid2>
      </Grid2>
    </Grid2>
  );
}
